"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  FaCheck,
  FaTimes,
  FaVideo,
  FaSpinner
} from "react-icons/fa"
import { Calendar, MapPin, Users, Filter, CalendarRange } from "lucide-react"

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
const backend_url = rawBackendUrl.startsWith("http://") || rawBackendUrl.startsWith("https://") ? rawBackendUrl : `https://${rawBackendUrl}`;
const API_URL = `${backend_url}/api`

const formatDateTime = (dateStr) => {
  return new Date(dateStr).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  })
}

const isPast = (dateStr, duree) => {
  const end = new Date(new Date(dateStr).getTime() + duree * 60000)
  return new Date() > end
}

const isNowMeeting = (dateStr, duree) => {
  const now = new Date()
  const start = new Date(dateStr)
  const end = new Date(start.getTime() + duree * 60000)
  return now >= start && now <= end
}

/* ================= EVENT CARD ================= */
const EventCard = ({ event, onJoin, onRespond, onViewDetails }) => {
  const [hover, setHover] = useState(false)
  const active = isNowMeeting(event.date_heure, event.duree)
  const passed = isPast(event.date_heure, event.duree)

  const getStatusStyle = () => {
    if (active) return { borderLeft: "4px solid #22c55e", background: "#f0fdf4" }
    if (passed) return { borderLeft: "4px solid #9ca3af", background: "#f9fafb", opacity: 0.8 }
    return { borderLeft: "4px solid #5e17eb" }
  }

  const getStatusBadge = (status) => {
    switch(status) {
      case 'accepte':
        return <span style={styles.badgeAccepte}>Accepté</span>
      case 'refuse':
        return <span style={styles.badgeRefuse}>Refusé</span>
      case 'en_attente':
        return <span style={styles.badgeAttente}>En attente</span>
      default:
        return null
    }
  }

  const handleCardClick = () => {
    // Rediriger vers les détails uniquement si la réunion est acceptée
    if (event.statut_participation === 'accepte') {
      onViewDetails(event.id)
    }
  }

  return (
    <div
      onClick={handleCardClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...styles.card,
        ...getStatusStyle(),
        transform: hover ? "translateY(-4px) scale(1.02)" : "translateY(0)",
        boxShadow: hover
          ? "0 8px 20px rgba(94,23,235,0.2)"
          : "0 2px 8px rgba(0,0,0,0.05)",
        cursor: event.statut_participation === 'accepte' ? "pointer" : "default",
      }}
    >
      <div style={styles.cardHeader}>
        <Calendar size={32} color="#5e17eb" />
      </div>

      <div style={styles.cardBody}>
        <div style={styles.cardHeaderRow}>
          <h3 style={styles.cardTitle}>{event.titre}</h3>
          {getStatusBadge(event.statut_participation)}
        </div>

        <p style={styles.cardOrganizer}>
          Organisé par: <strong>{event.organisateur_nom || event.organisateur}</strong>
        </p>

        <p style={styles.cardDate}>
          {formatDateTime(event.date_heure)} ({event.duree} min)
        </p>

        {event.objectif && (
          <p style={styles.cardObjective}>{event.objectif}</p>
        )}

        <div style={styles.cardInfo}>
          <div style={styles.infoItem}>
            <MapPin size={12} color="#666" />
            <span>{event.salle}</span>
          </div>
          <div style={styles.infoItem}>
            <Users size={12} color="#666" />
            <span>{event.participants_details?.length || 0} participants</span>
          </div>
        </div>

        {!passed && event.statut_participation === 'en_attente' && (
          <div style={styles.invitationActions}>
            <button
              onClick={(e) => { e.stopPropagation(); onRespond(event.id, 'accepte'); }}
              style={styles.acceptButton}
            >
              <FaCheck size={12} /> Accepter
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onRespond(event.id, 'refuse'); }}
              style={styles.refuseButton}
            >
              <FaTimes size={12} /> Refuser
            </button>
          </div>
        )}

        {event.statut_participation === 'accepte' && (
          <button
            disabled={!active}
            onClick={(e) => { e.stopPropagation(); onJoin(); }}
            style={{
              ...styles.joinButton,
              backgroundColor: active ? "#16a34a" : "#e5e7eb",
              color: active ? "white" : "#9ca3af",
              cursor: active ? "pointer" : "not-allowed",
            }}
          >
            <FaVideo size={14} />
            {active ? "Participer" : passed ? "Terminée" : "À venir"}
          </button>
        )}

        {event.statut_participation === 'refuse' && (
          <div style={styles.refusedMessage}>
            Vous avez refusé cette invitation
          </div>
        )}
      </div>
    </div>
  )
}

/* ================= MAIN COMPONENT ================= */
export default function MesReunions() {
  const navigate = useNavigate()
  const [meetings, setMeetings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  
  const [timeFilter, setTimeFilter] = useState("all")
  const [statusFilter, setStatusFilter] = useState("all")

  const getToken = () => localStorage.getItem("access_token")
  const authData = JSON.parse(localStorage.getItem("auth_data") || "{}")

  const fetchMeetings = async () => {
    try {
      const token = getToken()
      if (!token) {
        setError("Vous devez être connecté")
        setLoading(false)
        return
      }

      const response = await fetch(`${API_URL}/reunions/`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      })

      if (!response.ok) throw new Error("Erreur de chargement")

      const data = await response.json()
      
      let meetingsData = []
      if (data.results) meetingsData = data.results
      else if (Array.isArray(data)) meetingsData = data
      else if (data.data) meetingsData = data.data

      setMeetings(meetingsData)
    } catch (err) {
      console.error("Erreur:", err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMeetings()

    const handleInvitationUpdate = () => fetchMeetings()
    window.addEventListener('invitation-updated', handleInvitationUpdate)
    return () => window.removeEventListener('invitation-updated', handleInvitationUpdate)
  }, [])

  const handleInvitationResponse = async (meetingId, reponse) => {
    try {
      const token = getToken()
      const meeting = meetings.find(m => m.id === meetingId)
      if (!meeting) return

      const participation = meeting.participations?.find(
        p => p.utilisateur === authData?.userId
      )

      if (!participation) return

      const response = await fetch(`${API_URL}/participations/${participation.id}/repondre/`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ statut: reponse })
      })

      if (response.ok) {
        setMeetings(prev =>
          prev.map(m => {
            if (m.id === meetingId) {
              return { ...m, statut_participation: reponse }
            }
            return m
          })
        )
        window.dispatchEvent(new Event('invitation-updated'))
      }
    } catch (err) {
      console.error("Erreur réponse invitation:", err)
    }
  }

  const handleViewDetails = (meetingId) => {
    navigate(`/reunions/${meetingId}`)
  }

  // Filtrer et trier les réunions (en cours d'abord, puis à venir, puis passées)
  const filteredMeetings = meetings
    .filter(m => {
      const now = new Date()
      const end = new Date(new Date(m.date_heure).getTime() + m.duree * 60000)
      
      switch(timeFilter) {
        case "upcoming": return end > now
        case "past": return end < now
        default: return true
      }
    })
    .filter(m => {
      if (statusFilter === "all") return true
      return m.statut_participation === statusFilter
    })
    .sort((a, b) => {
      const aNow = isNowMeeting(a.date_heure, a.duree)
      const bNow = isNowMeeting(b.date_heure, b.duree)
      const aPast = isPast(a.date_heure, a.duree)
      const bPast = isPast(b.date_heure, b.duree)
      
      if (aNow && !bNow) return -1
      if (!aNow && bNow) return 1
      
      if (!aPast && bPast) return -1
      if (aPast && !bPast) return 1
      
      return new Date(a.date_heure) - new Date(b.date_heure)
    })

  const stats = {
    total: meetings.length,
    upcoming: meetings.filter(m => {
      const end = new Date(new Date(m.date_heure).getTime() + m.duree * 60000)
      return end > new Date()
    }).length,
    past: meetings.filter(m => {
      const end = new Date(new Date(m.date_heure).getTime() + m.duree * 60000)
      return end < new Date()
    }).length,
    enAttente: meetings.filter(m => m.statut_participation === 'en_attente').length,
    accepte: meetings.filter(m => m.statut_participation === 'accepte').length,
    refuse: meetings.filter(m => m.statut_participation === 'refuse').length
  }

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p>Chargement de vos réunions...</p>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>MES RÉUNIONS</h1>
      <p style={styles.subtitle}>
        Gérez toutes vos réunions et invitations
      </p>

      <div style={styles.statsBar}>
        <div style={styles.statItem}>
          <span style={styles.statValue}>{stats.total}</span>
          <span style={styles.statLabel}>Total</span>
        </div>
        <div style={styles.statItem}>
          <span style={{...styles.statValue}}>{stats.upcoming}</span>
          <span style={styles.statLabel}>À venir</span>
        </div>
        <div style={styles.statItem}>
          <span style={{...styles.statValue}}>{stats.past}</span>
          <span style={styles.statLabel}>Passées</span>
        </div>
        <div style={styles.statItem}>
          <span style={{...styles.statValue}}>{stats.enAttente}</span>
          <span style={styles.statLabel}>En attente</span>
        </div>
        <div style={styles.statItem}>
          <span style={{...styles.statValue}}>{stats.accepte}</span>
          <span style={styles.statLabel}>Acceptées</span>
        </div>
        <div style={styles.statItem}>
          <span style={{...styles.statValue}}>{stats.refuse}</span>
          <span style={styles.statLabel}>Refusées</span>
        </div>
      </div>

      <div style={styles.topBar}>
        <div style={styles.filterGroup}>
          <CalendarRange size={16} color="#666" />
          <select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">Toutes les dates</option>
            <option value="upcoming">À venir ({stats.upcoming})</option>
            <option value="past">Passées ({stats.past})</option>
          </select>
        </div>

        <div style={styles.filterGroup}>
          <Filter size={16} color="#666" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">Tous les statuts</option>
            <option value="en_attente">En attente ({stats.enAttente})</option>
            <option value="accepte">Acceptées ({stats.accepte})</option>
            <option value="refuse">Refusées ({stats.refuse})</option>
          </select>
        </div>
      </div>

      {(timeFilter !== "all" || statusFilter !== "all") && (
        <div style={styles.filterSummary}>
          <span>Filtres actifs : </span>
          {timeFilter !== "all" && (
            <span style={styles.filterTag}>
              {timeFilter === "upcoming" ? "À venir" : "Passées"}
            </span>
          )}
          {statusFilter !== "all" && (
            <span style={styles.filterTag}>
              {statusFilter === "en_attente" ? "En attente" : 
               statusFilter === "accepte" ? "Acceptées" : "Refusées"}
            </span>
          )}
          <button 
            onClick={() => {
              setTimeFilter("all")
              setStatusFilter("all")
            }}
            style={styles.clearFilters}
          >
            Effacer
          </button>
        </div>
      )}

      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}

      {filteredMeetings.length === 0 ? (
        <div style={styles.emptyState}>
          <Calendar size={40} color="#9ca3af" />
          <p>Aucune réunion trouvée</p>
          {(timeFilter !== "all" || statusFilter !== "all") && (
            <button 
              onClick={() => {
                setTimeFilter("all")
                setStatusFilter("all")
              }}
              style={styles.resetButton}
            >
              Réinitialiser les filtres
            </button>
          )}
        </div>
      ) : (
        <div style={styles.grid}>
          {filteredMeetings.map((meeting) => (
            <EventCard
              key={meeting.id}
              event={meeting}
              onJoin={() => navigate(`/consulter-reunion/${meeting.id}`)}
              onRespond={handleInvitationResponse}
              onViewDetails={handleViewDetails}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const styles = {
  page: { 
    padding: "22px", 
    background: "#f7f6ff", 
    minHeight: "100vh",
    borderRadius: "16px"
  },
  title: {
    fontSize: "26px",
    color: "#1f1f2e",
    marginBottom: "4px",
    fontWeight: "500",
  },
  subtitle: {
    color: "#666",
    marginBottom: "18px",
    fontSize: "14px",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
    gap: "16px"
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "3px solid #f3f3f3",
    borderTop: "3px solid #5e17eb",
    borderRadius: "50%",
    animation: "spin 1s linear infinite"
  },
  statsBar: {
    display: "flex",
    gap: "24px",
    background: "white",
    padding: "16px 24px",
    borderRadius: "12px",
    marginBottom: "18px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    flexWrap: "wrap"
  },
  statItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: "70px"
  },
  statValue: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#1f1f2e"
  },
  statLabel: {
    fontSize: "11px",
    color: "#666",
    marginTop: "4px"
  },
  topBar: { 
    display: "flex", 
    gap: "12px",
    marginBottom: "18px",
    flexWrap: "wrap"
  },
  filterGroup: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    background: "white",
    padding: "4px 12px",
    borderRadius: "8px",
    border: "1px solid #e5e7eb"
  },
  filterSelect: {
    padding: "6px",
    border: "none",
    background: "transparent",
    outline: "none",
    cursor: "pointer",
    fontSize: "13px",
    minWidth: "130px"
  },
  filterSummary: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 16px",
    background: "#ede9ff",
    borderRadius: "8px",
    marginBottom: "18px",
    fontSize: "13px",
    flexWrap: "wrap"
  },
  filterTag: {
    background: "#5e17eb",
    color: "white",
    padding: "4px 10px",
    borderRadius: "16px",
    fontSize: "11px",
    fontWeight: "500"
  },
  clearFilters: {
    marginLeft: "auto",
    background: "transparent",
    border: "none",
    color: "#5e17eb",
    cursor: "pointer",
    fontSize: "12px",
    textDecoration: "underline",
    padding: "4px 8px"
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    background: "white",
    borderRadius: "12px",
    color: "#6b7280",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px"
  },
  resetButton: {
    background: "#5e17eb",
    color: "white",
    border: "none",
    padding: "8px 20px",
    borderRadius: "8px",
    fontSize: "13px",
    cursor: "pointer"
  },
  error: {
    background: "#fee2e2",
    color: "#ef4444",
    padding: "12px 16px",
    borderRadius: "8px",
    marginBottom: "18px"
  },
  grid: { 
    display: "grid", 
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", 
    gap: "16px"
  },
  card: {
    background: "white",
    borderRadius: "12px",
    transition: "all 0.2s ease",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  cardHeader: { 
    height: "100px", 
    background: "#f3f0ff", 
    display: "flex", 
    alignItems: "center", 
    justifyContent: "center" 
  },
  cardBody: { 
    padding: "14px", 
    display: "flex", 
    flexDirection: "column", 
    flex: 1 
  },
  cardHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "6px",
    gap: "8px"
  },
  cardTitle: { 
    fontWeight: "600", 
    fontSize: "15px", 
    color: "#1f1f2e",
    margin: 0
  },
  cardOrganizer: {
    fontSize: "11px",
    color: "#5e17eb",
    marginBottom: "4px",
  },
  cardDate: { 
    fontSize: "12px", 
    color: "#888", 
    marginBottom: "6px" 
  },
  cardObjective: {
    fontSize: "12px",
    color: "#4b5563",
    marginBottom: "8px",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    lineHeight: "1.4"
  },
  cardInfo: {
    display: "flex",
    gap: "12px",
    marginBottom: "10px"
  },
  infoItem: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "11px",
    color: "#666"
  },
  badgeAccepte: {
    fontSize: "10px",
    padding: "3px 8px",
    background: "#dcfce7",
    color: "#16a34a",
    borderRadius: "12px",
    fontWeight: "500",
    whiteSpace: "nowrap"
  },
  badgeRefuse: {
    fontSize: "10px",
    padding: "3px 8px",
    background: "#fee2e2",
    color: "#ef4444",
    borderRadius: "12px",
    fontWeight: "500",
    whiteSpace: "nowrap"
  },
  badgeAttente: {
    fontSize: "10px",
    padding: "3px 8px",
    background: "#fef3c7",
    color: "#eab308",
    borderRadius: "12px",
    fontWeight: "500",
    whiteSpace: "nowrap"
  },
  invitationActions: {
    display: "flex",
    gap: "6px",
    marginTop: "8px"
  },
  acceptButton: {
    flex: 1,
    backgroundColor: "#22c55e",
    color: "white",
    border: "none",
    borderRadius: "6px",
    padding: "6px",
    fontSize: "11px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    cursor: "pointer"
  },
  refuseButton: {
    flex: 1,
    backgroundColor: "#ef4444",
    color: "white",
    border: "none",
    borderRadius: "6px",
    padding: "6px",
    fontSize: "11px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    cursor: "pointer"
  },
  joinButton: { 
    marginTop: "8px", 
    padding: "6px 0", 
    borderRadius: "6px", 
    fontWeight: "500", 
    display: "flex", 
    justifyContent: "center", 
    alignItems: "center", 
    gap: "6px", 
    border: "none",
    width: "100%",
    fontSize: "12px"
  },
  refusedMessage: {
    marginTop: "8px",
    textAlign: "center",
    fontSize: "11px",
    color: "#ef4444",
    padding: "8px",
    borderTop: "1px solid #e5e7eb",
    background: "#fef2f2",
    borderRadius: "6px"
  }
}

// Animation
const styleSheet = document.createElement("style")
styleSheet.innerText = `@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`
document.head.appendChild(styleSheet)